/**
 * Booking theme wrapper — sets CSS custom-property tokens on a host `<div>`
 * so every descendant booking component inherits the correct palette.
 *
 * Usage:
 * ```tsx
 * <BookingThemeRoot mode="dark">
 *   <BookingSectionClient … />
 * </BookingThemeRoot>
 * ```
 *
 * You can also pass a full `BookingTheme` object for fine-grained control:
 * ```tsx
 * <BookingThemeRoot theme={{ ...bookingLightTheme, colorPrimary: "#0066ff" }}>
 *   …
 * </BookingThemeRoot>
 * ```
 */
"use client";
import React from "react";
import type { CSSProperties, HTMLAttributes, ReactNode } from "react";
import type { BookingTheme, BookingThemeMode } from "./bookingTheme";
import { resolveBookingTheme, buildBookingCssVars } from "./bookingTheme";

export type BookingThemeRootProps = Omit<
  HTMLAttributes<HTMLDivElement>,
  "children" | "className" | "style"
> & {
  /** Quick switch: "light" | "dark". Ignored when `theme` is provided. */
  mode?: BookingThemeMode;
  /** Full custom theme — takes precedence over `mode`. */
  theme?: BookingTheme;
  /** Extra inline styles applied to the wrapper div. */
  style?: CSSProperties;
  /** Extra class name for the wrapper div. */
  className?: string;
  children?: ReactNode;
};

export function BookingThemeRoot({
  mode,
  theme,
  style,
  className,
  children,
  ...divProps
}: BookingThemeRootProps) {
  const resolved = resolveBookingTheme(theme ?? mode);
  const cssVars = buildBookingCssVars(resolved);
  const classes = ["openings-booking-theme-root", className]
    .filter(Boolean)
    .join(" ");

  return (
    <div
      {...divProps}
      className={classes}
      style={{
        ...cssVars,
        ...style,
      }}
    >
      {children}
    </div>
  );
}
